import type { Option, OptionGroup } from "@/types";

export type PriceMethod = OptionGroup["priceMethod"];

/** optionId -> quantity selected within a single option group. */
export type GroupQuantities = Record<string, number>;

function num(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function selectedOptions(options: Option[], quantities: GroupQuantities): Option[] {
  return options.filter((opt) => num(quantities[opt.id]) > 0);
}

/** Σ (price × quantity) over the selected options: SUM, and AVG over the slots of a portion group. */
export function linearGroupTotal(options: Option[], quantities: GroupQuantities): number {
  return options.reduce(
    (acc, opt) => acc + num(quantities[opt.id]) * num(opt.price.value),
    0,
  );
}

/** Quantity-weighted mean price of the selected options, charged once; 0 when nothing is selected. */
export function meanGroupPrice(options: Option[], quantities: GroupQuantities): number {
  const selected = selectedOptions(options, quantities);
  const totalQuantity = selected.reduce((acc, opt) => acc + num(quantities[opt.id]), 0);
  return totalQuantity > 0 ? linearGroupTotal(selected, quantities) / totalQuantity : 0;
}

/** Highest price among the selected options, charged once; 0 when nothing is selected. */
export function topGroupPrice(options: Option[], quantities: GroupQuantities): number {
  return selectedOptions(options, quantities).reduce((top, opt) => Math.max(top, num(opt.price.value)), 0);
}

/**
 * Group price contribution for (already portion-resolved) quantities.
 * `portionTarget` is set only for a portion group (pizza flavours), whose prices are per slot.
 */
export type GroupPriceStrategy = (
  options: Option[],
  quantities: GroupQuantities,
  portionTarget?: number,
) => number;

/** SUM adds every option; AVG/MAX charge the mean or the top price once, or bill the slots of a portion group. */
export const PRICE_STRATEGIES: Record<PriceMethod, GroupPriceStrategy> = {
  SUM: (options, quantities) => linearGroupTotal(options, quantities),
  AVG: (options, quantities, portionTarget) =>
    portionTarget ? linearGroupTotal(options, quantities) : meanGroupPrice(options, quantities),
  MAX: (options, quantities, portionTarget) => topGroupPrice(options, quantities) * (portionTarget || 1),
};
